import Page from '../Page';
import LoadingIndicator from '../components/LoadingIndicator';
import {ProblemsBadge} from '../components/ConfigurationHealth';
import {useEffect, useState} from 'preact/hooks';
import {useLocation} from 'preact-iso';
import {ensureConfiguratorDefinitions, listConfiguredProducts} from '../../../../shared/models/product-configurator';
import {productPath} from '../productRoutes';

export default function ProductsPage() {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    ensureConfiguratorDefinitions().then(listConfiguredProducts).then(setProducts)
      .catch((e) => setError(e instanceof Error ? e.message : 'Unable to load configured products.'))
      .finally(() => setStatus('idle'));
  }, []);

  const chooseProduct = async () => {
    const selected = await shopify.resourcePicker({type: 'product', multiple: false, action: 'select', filter: {draft: true, archived: false}});
    if (selected?.[0]?.id) location.route(productPath(selected[0].id));
  };

  return (
    <Page heading="Products">
      <s-button slot="primary-action" variant="primary" onClick={chooseProduct}>Configure product</s-button>
      {error && <s-banner tone="critical">{error}</s-banner>}
      <s-section heading="Configured products">
        {status === 'loading' ? <LoadingIndicator label="Loading configured products" /> : products.length ? (
          <s-table>
            <s-table-header-row><s-table-header listSlot="primary"><span style={{whiteSpace:'nowrap'}}>Product</span></s-table-header><s-table-header listSlot="labeled">Template</s-table-header><s-table-header><span style={{whiteSpace:'nowrap'}}>Options</span></s-table-header><s-table-header><span style={{whiteSpace:'nowrap'}}>Choices</span></s-table-header><s-table-header><span style={{whiteSpace:'nowrap'}}>Components</span></s-table-header></s-table-header-row>
            <s-table-body>{products.map((product) => (
              <s-table-row key={product.id}>
                <s-table-cell>
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-link tone="neutral" href={productPath(product.id)} onClick={(event) => { event.preventDefault(); location.route(productPath(product.id)); }}><s-text>{product.title}</s-text></s-link>
                    <ProblemsBadge health={product.health} draftHealth={product.draftHealth} />
                  </s-stack>
                </s-table-cell>
                <s-table-cell>{product.templateSuffix || 'default'}</s-table-cell><s-table-cell>{product.summary.options}</s-table-cell><s-table-cell>{product.summary.choices}</s-table-cell><s-table-cell>{product.summary.components}</s-table-cell>
              </s-table-row>
            ))}</s-table-body>
          </s-table>
        ) : <s-stack alignItems="center" gap="base" padding="large-400"><s-heading>No configured products yet</s-heading><s-paragraph>Choose a product to configure its native options, requirements, and custom options.</s-paragraph><s-button onClick={chooseProduct}>Configure product</s-button></s-stack>}
      </s-section>
      <s-section heading="How it works">
        <s-stack gap="base">
          <s-paragraph>This app lets you configure product pages with complex options and detailed information.</s-paragraph>
          <s-ordered-list>
            <s-list-item><s-text type="strong" fontWeight="bold">Configure a product</s-text> in the app. Configuration is saved in metafields.</s-list-item>
            <s-list-item><s-text type="strong" fontWeight="bold">Preview a draft</s-text> of the configured page before publishing.</s-list-item>
            <s-list-item><s-text type="strong" fontWeight="bold">Publish the configuration</s-text> when you're done.</s-list-item>
            <s-list-item><s-text type="strong" fontWeight="bold">Change the theme template</s-text> to <code>product.configurator</code> to make it live.</s-list-item>
          </s-ordered-list>
        </s-stack>
      </s-section>
    </Page>
  );
}
